/**
 * Chat surface: the conversation as bubbles, with a composer under it.
 *
 * The bubbles are <deepagent-transcript>, the same element a call uses for
 * its live transcript; this element adds the form. With `composer` off it is
 * that transcript and nothing else, so the panel renders one element for
 * every surface and only says whether writing is on.
 *
 * Owns no state at all. The host feeds it the transcript, whether a reply is
 * pending and the `draft` in the composer; it reports typing as
 * `draft-change` and a submit as `send-message`, and the host decides what
 * becomes of both. Enter sends, Shift+Enter breaks the line, and the send
 * button is the same action for anyone on a touch keyboard.
 *
 * The composer works before a session exists: writing is what opens a chat, so
 * the first message is sent the same way as every one after it. While the
 * session is opening, sending is held back but typing is not.
 */

import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { live } from "lit/directives/live.js";

import "./deepagent-transcript.js";

import { sendIcon } from "./icons.js";

import type { TranscriptEntry } from "../state/chat-thread.js";

@customElement("deepagent-chat")
export class DeepAgentChat extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
      color: var(--da-text);
      font-family: inherit;
    }
    form {
      padding: 8px 16px 12px;
    }
    .field {
      display: flex;
      gap: 8px;
      align-items: flex-end;
      padding: 4px 4px 4px 12px;
      border: 1px solid var(--da-input);
      border-radius: var(--da-radius-lg, 2px);
      background: var(--da-bg);
      transition: border-color 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    .field:focus-within {
      border-color: var(--da-accent);
      box-shadow: 0 0 0 1px var(--da-accent);
    }
    textarea {
      flex: 1 1 auto;
      resize: none;
      max-height: 120px;
      min-height: 36px;
      padding: 8px 0;
      border: none;
      outline: none;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 14px;
      line-height: 20px;
    }
    textarea::placeholder {
      color: var(--da-text-muted);
    }
    .send {
      flex: 0 0 auto;
      width: 36px;
      height: 36px;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: var(--da-radius, 2px);
      background: var(--da-accent);
      color: var(--da-accent-fg);
      cursor: pointer;
      transition:
        opacity 180ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    .send:active:not(:disabled) {
      transform: scale(0.96);
      transition-duration: 90ms;
    }
    .send svg {
      width: 18px;
      height: 18px;
    }
    .send:focus-visible {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }
    .send:disabled {
      opacity: 0.35;
      cursor: default;
    }
    /* Under 16px iOS zooms the page on focus; a fingertip needs a bigger send. */
    @media (pointer: coarse), (max-width: 640px) {
      textarea { font-size: 16px; line-height: 22px; }
      .send, textarea { -webkit-tap-highlight-color: transparent; }
    }
    @media (pointer: coarse) {
      .send { width: 44px; height: 44px; }
    }
  `;

  @property({ type: Array }) entries: TranscriptEntry[] = [];
  @property({ type: Boolean }) awaitingReply = false;
  /** Whether writing is on. Off, this is the transcript alone. */
  @property({ type: Boolean }) composer = false;
  /** What is in the composer. Controlled: typing asks the host to change it. */
  @property({ type: String }) draft = "";
  /** True until the session is connected, and again once it has ended. */
  @property({ type: Boolean }) disabled = false;
  @property({ type: String }) labelUser = "You";
  @property({ type: String }) labelAi = "Agent";
  @property({ type: String }) placeholder = "";
  @property({ type: String }) sendLabel = "Send";
  @property({ type: String }) typingLabel = "";
  @property({ type: String }) hint = "";
  /** True while the session is opening: typing stays open, sending waits. */
  @property({ type: Boolean }) connecting = false;

  @query("textarea") private textarea!: HTMLTextAreaElement | null;

  /**
   * Awaits this element's own render: the panel calls in right after its own,
   * one microtask before this shadow root has a textarea to focus.
   */
  async focusComposer(): Promise<void> {
    await this.updateComplete;
    this.textarea?.focus();
  }

  protected override updated(changed: PropertyValues<this>): void {
    // A first render (old value undefined) is the panel opening: the textarea
    // keeps its resting height and the panel places focus itself.
    if (changed.has("draft") && changed.get("draft") !== undefined) {
      this.fitComposer();
      // Words that arrive from outside while nobody is typing are a failed
      // start handing them back: the caret goes with them, so Enter retries.
      // Typing already holds focus.
      if (this.draft && this.shadowRoot?.activeElement !== this.textarea) {
        void this.focusComposer();
      }
    }
  }

  /** Grow with the text, up to the cap the stylesheet sets. */
  private fitComposer(): void {
    const el = this.textarea;
    if (!el) return;
    el.style.height = "";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }

  private submit(): void {
    const text = this.draft.trim();
    if (!text || this.disabled || this.connecting) return;
    // Whether the words leave the composer is the host's call: it clears the
    // draft for a message it took, and leaves it for one it could not.
    this.dispatchEvent(
      new CustomEvent<string>("send-message", {
        detail: text,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onInput = (event: Event): void => {
    const el = event.target as HTMLTextAreaElement;
    this.fitComposer();
    this.dispatchEvent(
      new CustomEvent<string>("draft-change", {
        detail: el.value,
        bubbles: true,
        composed: true,
      }),
    );
  };

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  };

  override render() {
    return html`
      <deepagent-transcript
        .entries=${this.entries}
        .awaitingReply=${this.awaitingReply}
        .labelUser=${this.labelUser}
        .labelAi=${this.labelAi}
        .typingLabel=${this.typingLabel}
        .hint=${this.hint}
      ></deepagent-transcript>
      ${this.composer ? this.renderComposer() : null}
    `;
  }

  private renderComposer() {
    const canSend =
      this.draft.trim().length > 0 && !this.disabled && !this.connecting;
    return html`
      <form @submit=${(e: Event) => { e.preventDefault(); this.submit(); }}>
        <div class="field">
          <textarea
            rows="1"
            .value=${live(this.draft)}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            aria-label=${this.placeholder}
            @input=${this.onInput}
            @keydown=${this.onKeydown}
          ></textarea>
          <button
            type="submit"
            class="send"
            aria-label=${this.sendLabel}
            title=${this.sendLabel}
            ?disabled=${!canSend}
          >
            ${sendIcon()}
          </button>
        </div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-chat": DeepAgentChat;
  }
}
